import React, { useState, useCallback, useEffect, useRef, Fragment } from 'react';
import BrandLoader from '../../shared/brand/BrandLoader';
import { useTranslation } from 'react-i18next';
import { Popover, Transition } from '@headlessui/react';
import apis from '../../../utils/apis';
import Button from '../../shared/TailwindComponents/Button';
import Toggle from '../../shared/TailwindComponents/Toggle';
import {
  PiSparkleLight,
  PiScalesLight,
  PiGavelLight,
  PiBookOpenLight,
  PiScrollLight,
  PiBuildingsLight,
  PiCaretDownLight,
} from 'react-icons/pi';

interface SearchIndex {
  index_name: string;
  display_name: string;
  category: string;
  source_type: string;
}

/**
 * Maps a source tab id to the index categories that feed it.
 * Example: the "laws" tab should show indexes with category "legislation".
 */
const TAB_TO_CATEGORY: Record<string, string[]> = {
  laws: ['legislation'],
  cases: ['case_law'],
  preparatory_works: ['preparatory_works'],
  regulations: ['regulation'],
  agency_decisions: ['agency_decisions'],
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  legislation: <PiScalesLight className="w-3.5 h-3.5" />,
  case_law: <PiGavelLight className="w-3.5 h-3.5" />,
  preparatory_works: <PiBookOpenLight className="w-3.5 h-3.5" />,
  regulation: <PiScrollLight className="w-3.5 h-3.5" />,
  agency_decisions: <PiBuildingsLight className="w-3.5 h-3.5" />,
};

interface SourceGeneratePopoverProps {
  /** The matter's jurisdiction code (e.g. "SE") */
  jurisdiction?: string;
  /** The active source tab id (e.g. "laws", "cases", "preparatory_works") */
  sourceTab: string;
  /** Whether a generation is already running for this tab */
  isGenerating: boolean;
  /** Callback to trigger generation — receives optional selected index names */
  onGenerate: (selectedIndexes?: string[]) => void;
  /** Whether at least one case file has been uploaded */
  hasDocuments: boolean;
  /** Current item count in the list (0 = show "Generate", >0 = show "Find More") */
  currentListCount: number;
  /** Whether user has edit access */
  canEdit?: boolean;
}

/**
 * SourceGeneratePopover
 *
 * Renders a "Find More" / "Generate" button for each source tab. When multiple
 * search indexes are available for the tab's category, shows a popover allowing
 * the user to either search all indexes or pick specific ones.
 *
 * When only 1 index exists → simple button (no popover).
 * When 0 indexes exist → returns null.
 */
const SourceGeneratePopover: React.FC<SourceGeneratePopoverProps> = ({
  jurisdiction,
  sourceTab,
  isGenerating,
  onGenerate,
  hasDocuments,
  currentListCount,
  canEdit = true,
}) => {
  const { t } = useTranslation();
  const [availableIndexes, setAvailableIndexes] = useState<SearchIndex[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndexes, setSelectedIndexes] = useState<Set<string>>(new Set());
  const fetchedForRef = useRef<string>('');

  // Fetch available indexes for the jurisdiction
  const fetchIndexes = useCallback(async (country: string) => {
    if (!country || fetchedForRef.current === country) return;
    fetchedForRef.current = country;
    setLoading(true);
    try {
      const response = await apis({
        method: 'GET',
        url: '/matters/available-indexes',
        params: { country },
      });
      const indexes: SearchIndex[] = response?.data?.indexes || [];
      setAvailableIndexes(indexes);
    } catch {
      setAvailableIndexes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (jurisdiction) {
      fetchIndexes(jurisdiction);
    }
  }, [jurisdiction, fetchIndexes]);

  // Filter indexes to those matching the current source tab's categories
  const categories = TAB_TO_CATEGORY[sourceTab] || [];
  const filteredIndexes = availableIndexes.filter((idx) =>
    categories.includes(idx.category)
  );

  // Reset selection when filtered indexes change
  useEffect(() => {
    setSelectedIndexes(new Set(filteredIndexes.map((idx) => idx.index_name)));
  }, [filteredIndexes.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // If loading or no indexes, render nothing
  if (loading || filteredIndexes.length === 0) return null;

  const buttonLabel = currentListCount === 0 ? t('Generate') : t('Find More');

  // While generating, show a disabled button with a spinner
  if (isGenerating) {
    return (
      <Button
        variant="primary"
        size="small"
        disabled
        icon={
          <BrandLoader size="xs" className="!h-3.5 !w-3.5" />
        }
      >
        {t('Searching...')}
      </Button>
    );
  }
  const disabledTooltip = !hasDocuments
    ? t('Upload at least one case file to enable AI generation.')
    : '';

  // Single index → simple button, no popover
  if (filteredIndexes.length === 1) {
    return (
      <Button
        variant="primary"
        size="small"
        onClick={() => onGenerate(filteredIndexes.map((i) => i.index_name))}
        disabled={!hasDocuments || !canEdit}
        tooltip={disabledTooltip}
        icon={<PiSparkleLight className="h-3.5 w-3.5" />}
      >
        {buttonLabel}
      </Button>
    );
  }

  // Multiple indexes → popover
  const toggleIndex = (indexName: string) => {
    setSelectedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(indexName)) {
        next.delete(indexName);
      } else {
        next.add(indexName);
      }
      return next;
    });
  };

  const allSelected = selectedIndexes.size === filteredIndexes.length;

  const handleSearchAll = (close: () => void) => {
    onGenerate(filteredIndexes.map((i) => i.index_name));
    close();
  };

  const handleSearchSelected = (close: () => void) => {
    if (selectedIndexes.size === 0) return;
    onGenerate(Array.from(selectedIndexes));
    close();
  };

  return (
    <Popover className="relative">
      <Popover.Button
        as={Button}
        variant="primary"
        size="small"
        disabled={!hasDocuments || !canEdit}
        tooltip={disabledTooltip}
        icon={<PiSparkleLight className="h-3.5 w-3.5" />}
      >
        <span className="flex items-center gap-1">
          {buttonLabel}
          <PiCaretDownLight className="h-3 w-3" />
        </span>
      </Popover.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Popover.Panel className="absolute right-0 z-50 mt-2 w-72 origin-top-right rounded-lg bg-surface shadow-lg ring-1 ring-black/5 dark:ring-white/10 p-3">
          {({ close }) => (
            <div className="space-y-3">
              {/* Header */}
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                  {t('Select Sources')}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (allSelected) {
                      setSelectedIndexes(new Set());
                    } else {
                      setSelectedIndexes(new Set(filteredIndexes.map((i) => i.index_name)));
                    }
                  }}
                  className="text-[11px] text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                >
                  {allSelected ? t('Deselect All') : t('Select All')}
                </button>
              </div>

              {/* Index list */}
              <div className="space-y-1">
                {filteredIndexes.map((idx) => {
                  const isSelected = selectedIndexes.has(idx.index_name);
                  const icon = CATEGORY_ICONS[idx.category];
                  return (
                    <div
                      key={idx.index_name}
                      className={`flex items-center justify-between gap-2 rounded-lg border p-2 transition-all duration-200 cursor-pointer ${
                        isSelected
                          ? 'border-primary-300 bg-primary-50/50 dark:border-primary-700 dark:bg-primary-900/20'
                          : 'border-hairline bg-surface'
                      }`}
                      onClick={() => toggleIndex(idx.index_name)}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {icon && (
                          <span className={`flex-shrink-0 transition-colors ${
                            isSelected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
                          }`}>
                            {icon}
                          </span>
                        )}
                        <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                          {idx.display_name}
                        </p>
                      </div>
                      <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <Toggle
                          enabled={isSelected}
                          onToggle={() => toggleIndex(idx.index_name)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-1.5 pt-1 border-t border-hairline">
                <Button
                  variant="primary"
                  size="small"
                  className="w-full"
                  onClick={() => handleSearchSelected(close)}
                  disabled={selectedIndexes.size === 0}
                  icon={<PiSparkleLight className="h-3.5 w-3.5" />}
                >
                  {selectedIndexes.size === filteredIndexes.length
                    ? t('Search All {{count}} Sources', { count: filteredIndexes.length })
                    : t('Search {{count}} Selected', { count: selectedIndexes.size })}
                </Button>
              </div>
            </div>
          )}
        </Popover.Panel>
      </Transition>
    </Popover>
  );
};

export default SourceGeneratePopover;
